/* eslint-disable no-console */
import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';

import { DinosaurRedirector, CookieConsentBanner } from './components/common';
import GoogleAnalytics from './components/common/GoogleAnalytics';
import { Footer, Header, ScrollToTop } from './components/layout';
import { UnifiedLoadingSpinner } from './components/ui';
import { AuthProvider, ToastProvider, useToast, useAuth } from './contexts';
import { usePerformanceOptimization } from './hooks';

// Lazy load pages to reduce bundle size
const AuthCallback = lazy(() => import('./pages/auth/AuthCallback'));
const AuthPage = lazy(() => import('./pages/auth/AuthPage'));
const ClassificationPage = lazy(() => import('./pages/creatures/ClassificationPage'));
const ClassificationDetailPage = lazy(() => import('./pages/creatures/ClassificationDetailPage'));
const DictionaryPage = lazy(() => import('./pages/creatures/DictionaryPage'));
const DinosaurDetailPage = lazy(() => import('./pages/creatures/DinosaurDetailPage'));
const AboutPage = lazy(() => import('./pages/general/AboutPage'));
const GalleryPage = lazy(() => import('./pages/general/GalleryPage'));
const HomePage = lazy(() => import('./pages/general/HomePage'));
const PrivacyPolicyPage = lazy(() => import('./pages/general/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/general/TermsOfServicePage'));
const SitemapPage = lazy(() => import('./pages/general/SitemapPage'));
const AgeDetailPage = lazy(() => import('./pages/geology/AgeDetailPage'));
const CretaceousPage = lazy(() => import('./pages/geology/CretaceousPage'));
const EraPage = lazy(() => import('./pages/geology/EraPage'));
const JurassicPage = lazy(() => import('./pages/geology/JurassicPage'));
const TriassicPage = lazy(() => import('./pages/geology/TriassicPage'));
const ArticleDetailPage = lazy(() => import('./pages/news/ArticleDetailPage'));
const KeywordPage = lazy(() => import('./pages/news/KeywordPage'));
const NewsPage = lazy(() => import('./pages/news/NewsPage'));
const FavoritesPage = lazy(() => import('./pages/user/FavoritesPage'));
const UserSettingsPage = lazy(() => import('./pages/user/UserSettingsPage'));
const AccountSettingsPage = lazy(() => import('./pages/user/AccountSettingsPage'));
const SocialConnectionsPage = lazy(() => import('./pages/user/SocialConnectionsPage'));
const NotificationSettingsPage = lazy(() => import('./pages/user/NotificationSettingsPage'));

// Google Analyticsを使用するコンポーネント
const AppContent: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const { user } = useAuth();
  const [processedUrl, setProcessedUrl] = React.useState<string>('');

  // パフォーマンス最適化フックを使用
  usePerformanceOptimization({
    enableMemoryMonitoring: false, // メモリ監視を一時的に無効化
    enableImageOptimization: true,
    enableBundleAnalysis: false, // CSS分析を無効化
    memoryThreshold: 200, // 200MBで警告
  });

  // メールアドレス確認完了時の処理（Supabase認証のみに移行したため同期処理は不要）
  const syncUserAfterEmailConfirmation = React.useCallback(async () => {
    if (user) {
      try {
        // Directusへの同期は不要（Supabase IDを直接使用）
        console.log('User email confirmed:', user.email);
      } catch (error) {
        console.error('Error after email confirmation:', error);
      }
    }
  }, [user]);

  // エラーケースの処理
  const handleEmailConfirmationError = React.useCallback(
    (fragment: string, currentUrl: string) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('メールアドレス確認エラーを検知:', fragment);
      }
      const errorParams = new URLSearchParams(fragment.substring(1)); // #を除去
      const error = errorParams.get('error');
      const errorCode = errorParams.get('error_code');
      const errorDescription = errorParams.get('error_description');

      let errorMessage = 'メールアドレスの確認に失敗しました';

      if (error === 'access_denied' && errorCode === 'otp_expired') {
        errorMessage = 'メールアドレス確認リンクの有効期限が切れているか、すでに確認済みです';
      } else if (errorDescription) {
        // エラーの詳細がある場合は適切なメッセージに変換
        const decodedDescription = decodeURIComponent(errorDescription.replace(/\+/g, ' '));
        if (decodedDescription.includes('expired')) {
          errorMessage = 'メールアドレス確認リンクの有効期限が切れています';
        } else if (decodedDescription.includes('invalid')) {
          errorMessage = 'メールアドレス確認リンクが無効です';
        }
      }

      // 処理済みとしてマーク
      setProcessedUrl(currentUrl);

      setTimeout(() => {
        showError(errorMessage, 'メールアドレス確認エラー');
      }, 500);

      // URLフラグメントをクリアして履歴を更新
      window.history.replaceState({}, '', window.location.pathname);
      if (process.env.NODE_ENV === 'development') {
        console.log('メールアドレス確認エラー処理完了');
      }
    },
    [showError, setProcessedUrl]
  );

  // フラグメントでの確認完了処理
  const handleFragmentConfirmation = React.useCallback(
    (fragment: string, currentUrl: string) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('メールアドレス確認完了を検知 (フラグメント):', fragment);
      }
      // 処理済みとしてマーク
      setProcessedUrl(currentUrl);

      // 少し遅延させてトーストを表示（認証状態の更新を待つ）
      setTimeout(() => {
        showSuccess('メールアドレスの確認が完了しました！', '確認完了');
        syncUserAfterEmailConfirmation();
      }, 1000);

      // URLフラグメントをクリアして履歴を更新
      window.history.replaceState({}, '', window.location.pathname);
      if (process.env.NODE_ENV === 'development') {
        console.log('メールアドレス確認完了処理完了 (フラグメント)');
      }
    },
    [showSuccess, setProcessedUrl, syncUserAfterEmailConfirmation]
  );

  // パラメータでの確認完了処理
  const handleParameterConfirmation = React.useCallback(
    (urlParams: URLSearchParams, currentUrl: string) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('メールアドレス確認完了を検知 (パラメータ):', urlParams.toString());
      }
      // 処理済みとしてマーク
      setProcessedUrl(currentUrl);

      // 少し遅延させてトーストを表示（認証状態の更新を待つ）
      setTimeout(() => {
        showSuccess('メールアドレスの確認が完了しました！', '確認完了');
        syncUserAfterEmailConfirmation();
      }, 1000);

      // URLパラメータをクリア
      urlParams.delete('confirmed');
      const newUrl = urlParams.toString()
        ? `${window.location.pathname}?${urlParams.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
      if (process.env.NODE_ENV === 'development') {
        console.log('メールアドレス確認完了処理完了 (パラメータ)');
      }
    },
    [showSuccess, setProcessedUrl, syncUserAfterEmailConfirmation]
  );

  // メールアドレス確認完了の検知
  useEffect(() => {
    // locationの値を直接取得して、依存配列から除外
    const currentLocation = window.location;
    const urlParams = new URLSearchParams(currentLocation.search);
    const fragment = currentLocation.hash;
    const currentUrl = currentLocation.pathname + currentLocation.search + currentLocation.hash;

    // フラグメントやパラメータに確認関連の情報が含まれていない場合は早期リターン
    const hasConfirmationInfo =
      fragment.includes('type=') ||
      fragment.includes('error=') ||
      urlParams.get('confirmed') === 'true';

    if (!hasConfirmationInfo) {
      return;
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('メールアドレス確認処理開始:', {
        currentUrl,
        processedUrl,
        hasFragment: !!fragment,
        hasError: fragment.includes('error='),
        hasSuccess: fragment.includes('type=') || urlParams.get('confirmed') === 'true',
      });
    }

    // 既に処理済みのURLの場合はスキップ
    if (processedUrl && processedUrl === currentUrl) {
      if (process.env.NODE_ENV === 'development') {
        console.log('既に処理済みのURLです:', currentUrl);
      }
      return;
    }

    // 現在のURLが空または無効な場合はスキップ
    if (!currentUrl || currentUrl === '/') {
      return;
    }

    // フラグを立てる変数
    let confirmationHandled = false;

    // エラーケースの処理
    if (fragment.includes('error=')) {
      handleEmailConfirmationError(fragment, currentUrl);
      return; // エラーの場合は成功処理をスキップ
    }

    // Supabaseのメールアドレス確認後のリダイレクトを検知
    // URLフラグメントに確認完了の情報が含まれている場合
    if (
      fragment.includes('type=signup') ||
      fragment.includes('type=email_change') ||
      fragment.includes('type=recovery')
    ) {
      handleFragmentConfirmation(fragment, currentUrl);
      confirmationHandled = true;
    }

    // URLパラメータでの確認完了検知（代替方法）
    // フラグメントで既に処理済みの場合はスキップ
    if (urlParams.get('confirmed') === 'true' && !confirmationHandled) {
      handleParameterConfirmation(urlParams, currentUrl);
    }
  }, [
    showSuccess,
    showError,
    user,
    syncUserAfterEmailConfirmation,
    handleEmailConfirmationError,
    handleFragmentConfirmation,
    handleParameterConfirmation,
    processedUrl,
  ]);

  return (
    <div className="flex flex-col min-h-screen">
      <ScrollToTop />
      <Header />
      <main className="flex-grow">
        <Suspense fallback={<UnifiedLoadingSpinner size="lg" color="green" variant="spinner" />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/creatures" element={<DictionaryPage />} />
            <Route path="/creatures/classifications" element={<ClassificationPage />} />
            <Route
              path="/creatures/classification/:classification"
              element={<ClassificationDetailPage />}
            />
            <Route path="/creatures/:documentId" element={<DinosaurDetailPage />} />
            {/* 古い /dinosaur/ パスからのリダイレクト */}
            <Route path="/dinosaur/:id" element={<DinosaurRedirector />} />
            <Route path="/era" element={<EraPage />} />
            <Route path="/era/age/:id" element={<AgeDetailPage />} />
            <Route path="/era/triassic" element={<TriassicPage />} />
            <Route path="/era/jurassic" element={<JurassicPage />} />
            <Route path="/era/cretaceous" element={<CretaceousPage />} />

            <Route path="/gallery" element={<GalleryPage />} />
            <Route path="/news" element={<NewsPage />} />
            <Route path="/news/keyword/:keyword" element={<KeywordPage />} />
            <Route path="/news/:documentId" element={<ArticleDetailPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/sitemap" element={<SitemapPage />} />
            <Route path="/favorites" element={<FavoritesPage />} />
            <Route path="/user/settings" element={<UserSettingsPage />} />
            <Route path="/user/account" element={<AccountSettingsPage />} />
            <Route path="/user/social-connections" element={<SocialConnectionsPage />} />
            <Route path="/user/notifications" element={<NotificationSettingsPage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
      <CookieConsentBanner />
    </div>
  );
};

function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <GoogleAnalytics />
          <AppContent />
        </Router>
      </AuthProvider>
    </ToastProvider>
  );
}

export default App;
